import { format } from "date-fns";
import { ArrowLeft, Check, ChevronDown, ChevronRight, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import EmptyState from "../../../shared/components/EmptyState";
import LoadingOverlay from "../../../shared/components/LoadingOverlay";
import PageHeader from "../../../shared/components/PageHeader";
import { useModerateOverlay, useOverlayDetail } from "../hooks/useOverlays";
import OverlayStatusBadge from "./OverlayStatusBadge";

const formatPrice = (priceArs?: number | null) => {
  if (priceArs == null || priceArs === 0) return "—";
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(priceArs);
};

const OverlayDetail = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  const { data: overlay, isLoading, isError, error } = useOverlayDetail(slug);
  const moderateMutation = useModerateOverlay();

  const [showTemplate, setShowTemplate] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [feedback, setFeedback] = useState<{
    kind: "success" | "error";
    message: string;
  } | null>(null);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t("OVERLAYS.detailTitle", "Overlay")} />
        <div className="py-12">
          <LoadingOverlay />
        </div>
      </div>
    );
  }

  if (isError || !overlay) {
    const isNotFound =
      error && typeof error === "object" && "response" in error
        ? (error as { response?: { status?: number } }).response?.status === 404
        : false;
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t("OVERLAYS.detailTitle", "Overlay")} />
        <EmptyState
          title={
            isNotFound
              ? t("OVERLAYS.notFoundTitle", "Overlay not found")
              : t("OVERLAYS.errorTitle", "Could not load overlay")
          }
          description={
            isNotFound
              ? t(
                  "OVERLAYS.notFoundDescription",
                  "The overlay you are looking for does not exist.",
                )
              : error instanceof Error
                ? error.message
                : t("OVERLAYS.errorDescription", "Try again in a moment.")
          }
          actions={
            <Button
              asChild
              variant="outline"
              className="border-neutral-700 text-neutral-300"
            >
              <Link to="/overlays">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("OVERLAYS.backToList", "Back to list")}
              </Link>
            </Button>
          }
        />
      </div>
    );
  }

  const handleApprove = () => {
    moderateMutation.mutate(
      { id: overlay.id, body: { decision: "approved" } },
      {
        onSuccess: () => {
          setFeedback({
            kind: "success",
            message: t("OVERLAYS.approvedToast", "Overlay approved."),
          });
          setTimeout(() => navigate("/overlays"), 600);
        },
        onError: (err) => {
          setFeedback({
            kind: "error",
            message:
              err instanceof Error
                ? err.message
                : t(
                    "OVERLAYS.moderationError",
                    "Could not save moderation decision.",
                  ),
          });
        },
      },
    );
  };

  const handleReject = () => {
    moderateMutation.mutate(
      {
        id: overlay.id,
        body: { decision: "rejected", note: rejectNote.trim() || undefined },
      },
      {
        onSuccess: () => {
          setRejectModalOpen(false);
          setFeedback({
            kind: "success",
            message: t("OVERLAYS.rejectedToast", "Overlay rejected."),
          });
          setTimeout(() => navigate("/overlays"), 600);
        },
        onError: (err) => {
          setFeedback({
            kind: "error",
            message:
              err instanceof Error
                ? err.message
                : t(
                    "OVERLAYS.moderationError",
                    "Could not save moderation decision.",
                  ),
          });
        },
      },
    );
  };

  const isPending = overlay.status === "pending";

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={overlay.name}
        subtitle={`v${overlay.version} • ${overlay.authorName}`}
        actions={
          <div className="flex items-center gap-2">
            <Button
              asChild
              variant="outline"
              className="border-neutral-700 text-neutral-300"
            >
              <Link to="/overlays">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("OVERLAYS.backToList", "Back to list")}
              </Link>
            </Button>
            {isPending && (
              <>
                <Button
                  onClick={() => setRejectModalOpen(true)}
                  variant="outline"
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  disabled={moderateMutation.isPending}
                >
                  <X className="mr-2 h-4 w-4" />
                  {t("OVERLAYS.reject", "Reject")}
                </Button>
                <Button
                  onClick={handleApprove}
                  className="bg-green-600 text-white hover:bg-green-700"
                  disabled={moderateMutation.isPending}
                >
                  <Check className="mr-2 h-4 w-4" />
                  {t("OVERLAYS.approve", "Approve")}
                </Button>
              </>
            )}
          </div>
        }
      />

      {feedback && (
        <div
          className={
            feedback.kind === "success"
              ? "rounded-md border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-300"
              : "rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"
          }
        >
          {feedback.message}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-neutral-800 bg-neutral-900/50 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-neutral-100">
              {t("OVERLAYS.previewSection", "Preview")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <p className="mb-2 text-xs uppercase tracking-wide text-neutral-500">
                  {t("OVERLAYS.thumbnail", "Thumbnail")}
                </p>
                {overlay.previewThumbnailUrl ? (
                  <img
                    src={overlay.previewThumbnailUrl}
                    alt={overlay.name}
                    className="aspect-video w-full rounded-md border border-neutral-800 bg-neutral-950 object-contain"
                  />
                ) : (
                  <div className="flex aspect-video w-full items-center justify-center rounded-md border border-dashed border-neutral-800 bg-neutral-950 text-sm text-neutral-500">
                    {t("OVERLAYS.noPreview", "No preview")}
                  </div>
                )}
              </div>
              <div>
                <p className="mb-2 text-xs uppercase tracking-wide text-neutral-500">
                  {t("OVERLAYS.animatedPreview", "Animated preview")}
                </p>
                {overlay.previewAnimatedUrl ? (
                  <video
                    src={overlay.previewAnimatedUrl}
                    controls
                    loop
                    autoPlay
                    muted
                    className="aspect-video w-full rounded-md border border-neutral-800 bg-neutral-950 object-contain"
                  />
                ) : (
                  <div className="flex aspect-video w-full items-center justify-center rounded-md border border-dashed border-neutral-800 bg-neutral-950 text-sm text-neutral-500">
                    {t("OVERLAYS.noAnimated", "No animated preview")}
                  </div>
                )}
              </div>
            </div>

            {overlay.description && (
              <div>
                <p className="mb-2 text-xs uppercase tracking-wide text-neutral-500">
                  {t("OVERLAYS.description", "Description")}
                </p>
                <p className="whitespace-pre-wrap text-sm text-neutral-200">
                  {overlay.description}
                </p>
              </div>
            )}

            <div>
              <button
                type="button"
                onClick={() => setShowTemplate((s) => !s)}
                className="flex items-center gap-2 text-sm text-neutral-300 hover:text-neutral-100"
              >
                {showTemplate ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                {t("OVERLAYS.rawTemplate", "Raw template JSON")}
              </button>
              {showTemplate && (
                <pre className="mt-2 max-h-[400px] overflow-auto rounded-md border border-neutral-800 bg-neutral-950 p-3 text-xs text-neutral-300">
                  {JSON.stringify(overlay.template, null, 2)}
                </pre>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="h-fit border-neutral-800 bg-neutral-900/50">
          <CardHeader>
            <CardTitle className="text-neutral-100">
              {t("OVERLAYS.metadata", "Metadata")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Field
              label={t("OVERLAYS.colStatus", "Status")}
              value={<OverlayStatusBadge status={overlay.status} />}
            />
            <Field label={t("OVERLAYS.slug", "Slug")} value={overlay.slug} />
            <Field
              label={t("OVERLAYS.colCategory", "Category")}
              value={
                <Badge
                  variant="outline"
                  className="border-neutral-600 text-neutral-300"
                >
                  {overlay.category}
                </Badge>
              }
            />
            <Field
              label={t("OVERLAYS.colLicense", "License")}
              value={overlay.license}
            />
            <Field
              label={t("OVERLAYS.colPrice", "Price")}
              value={formatPrice(overlay.priceArs)}
            />
            <Field
              label={t("OVERLAYS.colAuthor", "Author")}
              value={overlay.authorName}
            />
            <Field
              label={t("OVERLAYS.installCount", "Install count")}
              value={overlay.installCount.toLocaleString("es-AR")}
            />
            <Field
              label={t("OVERLAYS.colSubmitted", "Submitted")}
              value={format(new Date(overlay.createdAt), "dd MMM yyyy HH:mm")}
            />
            <Field
              label={t("OVERLAYS.updatedAt", "Updated")}
              value={format(new Date(overlay.updatedAt), "dd MMM yyyy HH:mm")}
            />
            {overlay.tags.length > 0 && (
              <Field
                label={t("OVERLAYS.tags", "Tags")}
                value={
                  <div className="flex flex-wrap gap-1">
                    {overlay.tags.map((tag) => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className="border-neutral-700 text-neutral-300"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                }
              />
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={rejectModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setRejectModalOpen(false);
            setRejectNote("");
          }
        }}
      >
        <DialogContent className="border-neutral-800 bg-neutral-900">
          <DialogHeader>
            <DialogTitle className="text-neutral-100">
              {t("OVERLAYS.rejectModalTitle", "Reject overlay")}
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              {t(
                "OVERLAYS.rejectModalDescription",
                "Optionally include a note to share with the author.",
              )}
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder={t(
              "OVERLAYS.rejectNotePlaceholder",
              "Reason for rejection...",
            )}
            rows={4}
            className="w-full rounded-md border border-neutral-700 bg-neutral-800/50 p-3 text-sm text-neutral-200 placeholder:text-neutral-500"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectModalOpen(false);
                setRejectNote("");
              }}
              className="border-neutral-700 text-neutral-300 hover:bg-neutral-800"
            >
              {t("OVERLAYS.cancel", "Cancel")}
            </Button>
            <Button
              onClick={handleReject}
              disabled={moderateMutation.isPending}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {t("OVERLAYS.confirmReject", "Reject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface FieldProps {
  label: string;
  value: React.ReactNode;
}

const Field = ({ label, value }: FieldProps) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs uppercase tracking-wide text-neutral-500">
      {label}
    </span>
    <div className="text-neutral-200">{value}</div>
  </div>
);

export default OverlayDetail;
